/**
 * Link — shared types, Zod prop schema, and styling tokens.
 *
 * A real text hyperlink (an `<a href>` on web), underlined in every state so
 * it never relies on colour alone. Three shapes: `inline` (runs inside body
 * copy), `standalone` (a bold link on its own line) and `small` (14px, e.g.
 * legal and help links). A link that opens a new tab carries the
 * open-new-window glyph plus visually hidden text the host supplies.
 *
 * Distinct from `TextButton type="Link"`, which is a `<button>` styled as a
 * link: this component keeps browser link behaviour (open in new tab, copy
 * link address, `mailto:`).
 *
 * On web the component is a real anchor. On native it is an inline text run
 * announced as a link that hands its `href` back to the host on press; the
 * host decides how the target opens (system browser, in-app viewer, route).
 *
 * Source of truth: web registration v7 sheet, Web/Link
 * https://www.figma.com/design/hk9NaJrKRwkoGmmZ5rxbsE (node 45:5454)
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { typography } from '../../elements/typography';
import type { IconName } from '../../elements/icons/registry';

export const LinkVariant = z.enum(['inline', 'standalone', 'small']);
export type LinkVariant = z.infer<typeof LinkVariant>;

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const linkPropsSchema = z.object({
  label: z.string().optional(),
  href: z.string(),
  variant: LinkVariant.default('inline'),
  /** Opens the target in a new browsing context with the open-new-window glyph. */
  opensNewTab: z.boolean().default(false),
  /** Visually hidden text announcing the new tab; required with `opensNewTab`. */
  newTabLabel: z.string().optional(),
  /** Accessible name when the visible label alone is not enough. */
  accessibilityLabel: z.string().optional(),
  /** Analytics id; omitted falls back to the generic `ui.link.open`. */
  actionId: actionIdSchema.optional(),
  testID: z.string().optional(),
});

const URL_SCHEME = /^([a-z][a-z0-9+.-]*):/i;
const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

/**
 * A relative href, or one using a safe scheme; anything else (e.g.
 * `javascript:`) is rejected. Tabs and newlines are removed and leading or
 * trailing control characters and spaces are trimmed first, as browsers do
 * when parsing a URL, so `java\nscript:` cannot pass as relative.
 */
export function isSafeLinkHref(href: string): boolean {
  const withoutTabsOrNewlines = href.replace(/[\t\n\r]/g, '');
  // Trim C0 control characters and spaces (code points up to U+0020).
  let start = 0;
  let end = withoutTabsOrNewlines.length;
  while (start < end && withoutTabsOrNewlines.charCodeAt(start) <= 0x20) start += 1;
  while (end > start && withoutTabsOrNewlines.charCodeAt(end - 1) <= 0x20) end -= 1;
  const normalised = withoutTabsOrNewlines.slice(start, end);
  const scheme = URL_SCHEME.exec(normalised)?.[1];
  return scheme === undefined || SAFE_SCHEMES.has(scheme.toLowerCase());
}

export type LinkRuleInput = {
  label?: unknown;
  accessibilityLabel?: unknown;
  href?: unknown;
  opensNewTab?: unknown;
  newTabLabel?: unknown;
};

export const linkNewTabRule = (data: LinkRuleInput): boolean =>
  !data.opensNewTab || (typeof data.newTabLabel === 'string' && data.newTabLabel.length > 0);
export const linkSafeHrefRule = (data: LinkRuleInput): boolean =>
  typeof data.href === 'string' && isSafeLinkHref(data.href);
export const linkNameRule = (data: LinkRuleInput): boolean =>
  Boolean(data.label) || Boolean(data.accessibilityLabel);

export const LINK_NEW_TAB_ISSUE = {
  message: 'newTabLabel is required when opensNewTab is true',
  path: ['newTabLabel'],
};
export const LINK_SAFE_HREF_ISSUE = {
  message: 'href must be relative or use http, https, mailto or tel',
  path: ['href'],
};
export const LINK_NAME_ISSUE = {
  message: 'Link needs a label or an accessibilityLabel',
  path: ['label'],
};

/** Validation-only schema: a new-tab link must say so to screen readers. */
export const linkPropsRefinedSchema = linkPropsSchema
  .refine(linkNewTabRule, LINK_NEW_TAB_ISSUE)
  .refine(linkSafeHrefRule, LINK_SAFE_HREF_ISSUE)
  .refine(linkNameRule, LINK_NAME_ISSUE);

export type LinkProps = z.input<typeof linkPropsSchema>;

export const LINK_TYPOGRAPHY: Record<LinkVariant, (typeof typography)[keyof typeof typography]> = {
  inline: typography.b2Regular,
  standalone: typography.b2Bold,
  small: typography.b3Regular,
};

export const LINK_COLOR = colors.text.primary;
/** Gap between the label and the new-tab glyph. */
export const LINK_ICON_GAP = spacing.xs;
/** Rounds the focus ring drawn around the link. */
export const LINK_RADIUS = radius.sm;
export const LINK_ICON_SIZE = sizing.icon.xs;
export const LINK_NEW_TAB_ICON: IconName = 'open-new-window';
export const LINK_ICON_COLOR = colors.icon.primary;
// Figma draws the hover underline at a raw 2px with no bound variable; the
// stroke scale's `strong` step carries the same value.
export const LINK_HOVER_UNDERLINE_THICKNESS = sizing.stroke.strong;

export const LINK_NEW_TAB_TARGET = '_blank';
export const LINK_NEW_TAB_REL = 'noopener noreferrer';
